import {
  Button,
  Card,
  Col,
  Empty,
  Flex,
  List,
  Row,
  Select,
  Typography
} from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts"

import { StatusTag } from "../StatusTag"
import {
  formatDays,
  formatMoney,
  formatPercent,
  formatPoints,
  formatScore
} from "../format"
import { patternAction, patternText } from "./patterns"
import type { Block, MemberOverviewResponse, PatternLink } from "./types"
import type { TrendMetric } from "./use-member-url-state"

const { Text } = Typography

export const TREND_METRICS: TrendMetric[] = [
  "schedule_score",
  "financial_score",
  "on_time_percent",
  "avg_delay_days",
  "overrun_amount",
  "gap_amount",
  "margin_variance_points"
]

const MONEY_METRICS: TrendMetric[] = [
  "overrun_amount",
  "gap_amount",
  "margin_variance_points"
]

// A period with no measured project has no value: the chart leaves a gap and the
// table says so, never a zero (spec §2 "Trend and comparison rules").
export const metricValue = (
  block: Block,
  metric: TrendMetric
): number | null => {
  if (!block.projects) return null
  if (metric === "schedule_score")
    return block.schedule_score.available ? block.schedule_score.value : null
  if (metric === "financial_score")
    return block.financial_score.available ? block.financial_score.value : null
  return (block.summary[metric] as number | null) ?? null
}

export const formatMetric = (
  metric: TrendMetric,
  value: number | null
): string => {
  if (metric === "on_time_percent") return formatPercent(value)
  if (metric === "avg_delay_days") return formatDays(value)
  if (metric === "overrun_amount" || metric === "gap_amount")
    return formatMoney(value)
  if (metric === "margin_variance_points") return formatPoints(value)
  return value === null ? "—" : String(value)
}

interface ScoreCardProps {
  title: string
  value: string
  caption: string
  status?: React.ReactNode
}

function ScoreCard({ title, value, caption, status }: ScoreCardProps) {
  return (
    <Card size="small" className="h-full">
      <Text type="secondary">{title}</Text>
      <Flex align="baseline" gap={8}>
        <span className="text-2xl font-semibold leading-tight">{value}</span>
        {status}
      </Flex>
      <Text type="secondary" className="text-xs">
        {caption}
      </Text>
    </Card>
  )
}

interface Props {
  data: MemberOverviewResponse
  metric: TrendMetric
  onMetricChange: (metric: TrendMetric) => void
  onDrill: (link: PatternLink) => void
}

export function OverviewView({ data, metric, onMetricChange, onDrill }: Props) {
  const { t } = useTranslation()
  const s = data.summary
  const scoreStatus = (score: Block["schedule_score"]) =>
    score.available ? null : (
      <Text type="secondary" className="text-xs">
        {t("teamPerformance.status.insufficient")}
      </Text>
    )
  const comparatorName = t(
    `teamPerformance.member.compare.${data.filters.compare}`
  )
  const metricLabel = t(`teamPerformance.member.metrics.${metric}`)
  const hideMoney = !data.amounts_visible && MONEY_METRICS.includes(metric)

  const chartData = data.trend.map((point) => ({
    label: point.label,
    period: point.period,
    member: hideMoney ? null : metricValue(point, metric),
    comparator: hideMoney ? null : metricValue(point.comparator, metric),
    memberProjects: point.projects,
    comparatorProjects: point.comparator.projects
  }))

  return (
    <Flex vertical gap="middle">
      <Row gutter={[12, 12]}>
        <Col xs={12} md={8} xl={4}>
          <ScoreCard
            title={t("teamPerformance.member.scheduleScore")}
            value={formatScore(data.schedule_score)}
            caption={t("teamPerformance.member.sample", {
              count: data.schedule_score.sample_size
            })}
            status={scoreStatus(data.schedule_score)}
          />
        </Col>
        <Col xs={12} md={8} xl={4}>
          <ScoreCard
            title={t("teamPerformance.member.financialScore")}
            value={formatScore(data.financial_score)}
            caption={t("teamPerformance.member.sample", {
              count: data.financial_score.sample_size
            })}
            status={scoreStatus(data.financial_score)}
          />
        </Col>
        <Col xs={12} md={8} xl={4}>
          <ScoreCard
            title={t("teamPerformance.kpi.onTime")}
            value={formatPercent(s.on_time_percent)}
            caption={t("teamPerformance.kpi.onTimeCaption", {
              count: s.on_time,
              total: s.schedule_eligible
            })}
          />
        </Col>
        <Col xs={12} md={8} xl={4}>
          <ScoreCard
            title={t("teamPerformance.kpi.avgDelay")}
            value={formatDays(s.avg_delay_days)}
            caption={t("teamPerformance.kpi.avgDelayCaption")}
          />
        </Col>
        <Col xs={12} md={8} xl={4}>
          <ScoreCard
            title={t("teamPerformance.kpi.costOverrun")}
            value={data.amounts_visible ? formatMoney(s.overrun_amount) : "—"}
            caption={t("teamPerformance.kpi.projectsCaption", {
              count: s.overrun_projects
            })}
          />
        </Col>
        <Col xs={12} md={8} xl={4}>
          <ScoreCard
            title={t("teamPerformance.kpi.revenueGap")}
            value={data.amounts_visible ? formatMoney(s.gap_amount) : "—"}
            caption={t("teamPerformance.kpi.projectsCaption", {
              count: s.gap_projects
            })}
          />
        </Col>
      </Row>

      <Flex align="center" gap={8} wrap>
        <StatusTag status={data.status.value} reasons={data.status.reasons} />
        <Text type="secondary">
          {t("teamPerformance.member.companyLine", {
            schedule: formatScore(data.company.schedule_score),
            financial: formatScore(data.company.financial_score),
            count: data.company.projects
          })}
        </Text>
      </Flex>

      <Card
        size="small"
        title={t("teamPerformance.member.trendTitle", { metric: metricLabel })}
        extra={
          <Select<TrendMetric>
            aria-label={t("teamPerformance.member.metric")}
            className="min-w-48"
            value={metric}
            onChange={onMetricChange}
            options={TREND_METRICS.map((m) => ({
              value: m,
              label: t(`teamPerformance.member.metrics.${m}`)
            }))}
          />
        }
      >
        <div aria-hidden="true">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart
              data={chartData}
              margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              onClick={(state) => {
                const label = state?.activeLabel
                const point = data.trend.find((p) => p.label === label)
                if (point?.projects)
                  onDrill({ view: "projects", period: point.period })
              }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" />
              <YAxis
                allowDecimals={false}
                width={64}
                tickFormatter={(value: number) =>
                  MONEY_METRICS.includes(metric) &&
                  metric !== "margin_variance_points"
                    ? formatMoney(value)
                    : metric === "on_time_percent"
                      ? `${value}%`
                      : metric === "avg_delay_days"
                        ? `${value} d`
                        : String(value)
                }
              />
              <Tooltip
                formatter={(value, name, item) => {
                  const row = item?.payload as {
                    memberProjects: number
                    comparatorProjects: number
                  }
                  const count =
                    name === "member"
                      ? row?.memberProjects
                      : row?.comparatorProjects
                  return [
                    `${formatMetric(metric, value as number | null)} · ${t("teamPerformance.member.projectsShort", { count: count ?? 0 })}`,
                    name === "member" ? data.member.name : comparatorName
                  ]
                }}
              />
              <Legend
                formatter={(name) =>
                  name === "member" ? data.member.name : comparatorName
                }
              />
              <Line
                type="monotone"
                dataKey="member"
                stroke="#1f7a8c"
                strokeWidth={2}
                connectNulls={false}
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="comparator"
                stroke="#9aa5b1"
                strokeDasharray="5 4"
                connectNulls={false}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card
        size="small"
        className="mt-2"
        title={t("teamPerformance.member.tableTitle", { metric: metricLabel })}
      >
        <TrendTable data={data} metric={metric} onDrill={onDrill} />
      </Card>

      <Card size="small" title={t("teamPerformance.member.patternsTitle")}>
        {data.patterns.length ? (
          <List
            dataSource={data.patterns}
            renderItem={(pattern) => (
              <List.Item
                actions={[
                  <Button
                    key="view"
                    size="small"
                    onClick={() => onDrill(pattern.link)}
                  >
                    {patternAction(t, pattern)}
                  </Button>
                ]}
              >
                {patternText(t, pattern)}
              </List.Item>
            )}
          />
        ) : (
          <Empty description={t("teamPerformance.member.noPatterns")} />
        )}
      </Card>
    </Flex>
  )
}

const SCHEDULE_METRICS: TrendMetric[] = [
  "schedule_score",
  "on_time_percent",
  "avg_delay_days"
]

function difference(
  metric: TrendMetric,
  own: number | null,
  other: number | null
) {
  if (own === null || other === null) return "—"
  const diff = Math.round((own - other) * 10) / 10
  const sign = diff > 0 ? "+" : diff < 0 ? "−" : ""
  const magnitude = Math.abs(diff)
  if (metric === "on_time_percent") return `${sign}${magnitude} pts`
  if (metric === "avg_delay_days") return `${sign}${magnitude.toFixed(1)} d`
  if (metric === "overrun_amount" || metric === "gap_amount")
    return `${sign}${formatMoney(magnitude)}`
  if (metric === "margin_variance_points")
    return `${sign}${magnitude.toFixed(1)} pts`
  return `${sign}${magnitude}`
}

function TrendTable({
  data,
  metric,
  onDrill
}: {
  data: MemberOverviewResponse
  metric: TrendMetric
  onDrill: (link: PatternLink) => void
}) {
  const { t } = useTranslation()
  const comparator = t(`teamPerformance.member.compare.${data.filters.compare}`)
  const hideMoney = !data.amounts_visible && MONEY_METRICS.includes(metric)
  const value = (block: Block) =>
    hideMoney ? null : metricValue(block, metric)
  const coverage = (block: Block) =>
    SCHEDULE_METRICS.includes(metric)
      ? block.summary.schedule_eligible
      : block.summary.financial_eligible

  return (
    <div className="overflow-x-auto">
      <table
        aria-label={t("teamPerformance.member.trendTable")}
        className="w-full text-sm"
      >
        <thead>
          <tr className="text-left text-gray-500">
            <th className="py-1 pr-3 font-medium">
              {t("teamPerformance.member.period")}
            </th>
            <th className="py-1 pr-3 text-right font-medium">
              {t("teamPerformance.kpi.closed")}
            </th>
            <th className="py-1 pr-3 text-right font-medium">
              {t(`teamPerformance.member.metrics.${metric}`)}
            </th>
            <th className="py-1 pr-3 text-right font-medium">{comparator}</th>
            <th className="py-1 pr-3 text-right font-medium">
              {t("teamPerformance.member.difference")}
            </th>
            <th className="py-1 pr-3 text-right font-medium">
              {t("teamPerformance.member.coverage")}
            </th>
            <th className="py-1" />
          </tr>
        </thead>
        <tbody>
          {data.trend.map((point) => (
            <tr key={point.period} className="border-t border-gray-100">
              <td className="py-1 pr-3">{point.label}</td>
              <td className="py-1 pr-3 text-right">{point.projects}</td>
              {point.projects ? (
                <td className="py-1 pr-3 text-right">
                  {hideMoney ? "—" : formatMetric(metric, value(point))}
                </td>
              ) : (
                <td className="py-1 pr-3 text-right text-gray-500">
                  {t("teamPerformance.member.noClosedProjects")}
                </td>
              )}
              <td className="py-1 pr-3 text-right">
                {point.comparator.projects
                  ? formatMetric(metric, value(point.comparator))
                  : t("teamPerformance.member.unavailable")}
              </td>
              <td className="py-1 pr-3 text-right">
                {difference(metric, value(point), value(point.comparator))}
              </td>
              <td className="py-1 pr-3 text-right">
                {point.projects
                  ? t("teamPerformance.member.coverageValue", {
                      count: coverage(point),
                      total: point.projects
                    })
                  : "—"}
              </td>
              <td className="py-1 text-right">
                <Button
                  size="small"
                  type="link"
                  disabled={!point.projects}
                  onClick={() =>
                    onDrill({ view: "projects", period: point.period })
                  }
                >
                  {t("teamPerformance.member.viewProjectsOf", {
                    name: point.label
                  })}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
